import { DateHelpers } from '../helpers/DateHelpers';
import { IZiplogInfo } from '../models/IZiplogInfo';
import { Module } from '../Module';
import { DeleteZiplogRequest } from '../ServerApi/requests/DeleteZiplogRequest';
import { GenerateZiplogRequest } from '../ServerApi/requests/GenerateZiplogRequest';
import { GetZiplogInfoRequest, IGetZiplogInfoResult } from '../ServerApi/requests/GetZiplogInfoRequest';
import { GetZiplogUriRequest, IGetZiplogUriResult } from '../ServerApi/requests/GetZiplogUriRequest';
import { PollService } from './PollService';
import { ServerService } from './ServerService';
import { WindowLocationService } from './WindowLocationService';

/**
 * NONE:        Ziplog available on the server
 * INPROGRESS:  Ziplog being generated
 * COMPLETE:    Ziplog available and ready for download
 */
export class ZiplogState {
  static NONE = 'none';
  static INPROGRESS = 'inprogress';
  static COMPLETE = 'complete';
}

/**
 * Manage the ziplog on Server.
 */
export class ZiplogService {

  public static $inject = ['ServerService', '$q', 'WindowLocationService', 'PollService'];

  public static ALL_DAYS: number = 0;

  private ziplogInfo: IZiplogInfo;
  private cancelPoll: () => void;
  private ziplogPollPromise: ng.IPromise<IZiplogInfo>;

  constructor(private server: ServerService,
              private $q: ng.IQService,
              private windowLocationService: WindowLocationService,
              private pollService: PollService) {}

  /**
   * Initiate ziplog generation. Call getInfo() after calling generate() to get a
   * promise that resolves to the generated ZiplogInfo.
   */
  public generate(daysToGenerate: number): ng.IPromise<void> {

    // Translate 'daysToGenerate' to a number that the server understands
    //
    //   'daysToGenerate'     logsUpToDate
    //   ----------------     ------------
    //   ALL_DAYS             undefined
    //   1 day                0
    //   2 days               1
    //   3 days               2
    //   etc.
    //
    const logsUpToDate = daysToGenerate !== ZiplogService.ALL_DAYS
      ? daysToGenerate - 1
      : undefined;

    return this.server.sendRequest(new GenerateZiplogRequest({
        logsUpToDate: logsUpToDate
      })).then(angular.noop);
  }

  /**
   * Retrieve the ZiplogInfo if it is `inprogress` or `complete`.
   * If ziplog is in `inprogress`, promise will resolve when ziplog is `complete`.
   * Do not call if ziplogs are `none`. The promise will never be resolved.
   * Call getState() before calling getInfo() to verify that ziplogs are not `none`.
   */
  public getInfo(): ng.IPromise<IZiplogInfo> {

    // Resolve immediately if we already have the ZiplogInfo
    if (this.ziplogInfo) {
      return this.$q.when(this.ziplogInfo);
    }

    // If we're already polling for ZiplogInfo, return that promise
    if (this.ziplogPollPromise) {
      return this.ziplogPollPromise;
    }

    const isComplete = (result: IGetZiplogInfoResult): boolean => {
      return result.status === ZiplogState.COMPLETE;
    };

    const ziplogPoller = this.pollService.poll(new GetZiplogInfoRequest({}), isComplete);
    this.cancelPoll = () => ziplogPoller.cancel();
    this.ziplogPollPromise = ziplogPoller.promise.then((result: IGetZiplogInfoResult) => {
          // TODO: Ziplog error handling B154120
        this.ziplogPollPromise = null;
        return this.ziplogInfoFromResult(result);
      });

    return this.ziplogPollPromise;
  }

  /**
   * Get current state of ziplog on server.
   * See ZiplogState for all possible ziplogs states.
   */
  public getState(): ng.IPromise<string> {
    return this.server.sendRequest(new GetZiplogInfoRequest())
      .then((result: IGetZiplogInfoResult) => {
        // TODO: Ziplog error handling B154120
        // If ziplog is generated, save the ZiplogInfo
        if (result.status === ZiplogState.COMPLETE) {
          this.ziplogInfo = this.ziplogInfoFromResult(result);
        }

        return result.status;
      });
  }

  /**
   * Delete ziplog from the server
   */
  public remove(): ng.IPromise<void> {
    return this.server.sendRequest(new DeleteZiplogRequest())
      .then(() => {
        // TODO: Ziplog error handling B154120
        this.ziplogInfo = null;
      });
  }

  /**
   * Download the ziplog. First requests the download url, then downloads.
   */
  public download(): ng.IPromise<void> {
    return this.server.sendRequest(new GetZiplogUriRequest())
      .then((result: IGetZiplogUriResult) => {
        // TODO: Ziplog error handling
        this.windowLocationService.download(result.data);
      });
  }

  /**
   * Stop polling.
   */
  public stopPolling(): void {
    if (this.ziplogPollPromise) {
      this.cancelPoll();
      this.ziplogPollPromise = null;
    }
  }

  private ziplogInfoFromResult(result: IGetZiplogInfoResult): IZiplogInfo {
    const ziplog: IZiplogInfo = {
      size : result.size,
      date : DateHelpers.fromISOString(result.date)
    };
    return ziplog;
  }

}

Module.instance().service('ZiplogService', ZiplogService);
